/**
 * Firebase / Google Analytics 4 for the storefront.
 *
 * The Firebase web config is a set of public identifiers (it ships in every
 * visitor's browser), so it is read from VITE_FIREBASE_* at build time like
 * VITE_API_URL - see apps/web/.env.production. Leave them unset (local dev,
 * tests) and analytics stays off, so development traffic never reaches the
 * real property.
 *
 * The SDK is loaded on first use, in its own chunk, so it never delays the
 * first paint.
 */

export interface FirebaseWebConfig {
  apiKey: string;
  authDomain?: string;
  projectId: string;
  storageBucket?: string;
  messagingSenderId?: string;
  appId: string;
  measurementId: string;
}

export function firebaseConfig(env: Record<string, string | undefined> = import.meta.env): FirebaseWebConfig | null {
  const apiKey = env.VITE_FIREBASE_API_KEY?.trim();
  const projectId = env.VITE_FIREBASE_PROJECT_ID?.trim();
  const appId = env.VITE_FIREBASE_APP_ID?.trim();
  const measurementId = env.VITE_FIREBASE_MEASUREMENT_ID?.trim();
  if (!apiKey || !projectId || !appId || !measurementId) return null;
  return {
    apiKey,
    projectId,
    appId,
    measurementId,
    authDomain: env.VITE_FIREBASE_AUTH_DOMAIN?.trim() || undefined,
    storageBucket: env.VITE_FIREBASE_STORAGE_BUCKET?.trim() || undefined,
    messagingSenderId: env.VITE_FIREBASE_MESSAGING_SENDER_ID?.trim() || undefined,
  };
}

/** Browsers that ask not to be tracked are not tracked. */
function doNotTrack(): boolean {
  const nav = typeof navigator === 'undefined' ? undefined : navigator;
  return nav?.doNotTrack === '1' || (globalThis as { doNotTrack?: string }).doNotTrack === '1';
}

type Logger = (name: string, params: Record<string, unknown>) => void;

let logger: Promise<Logger | null> | undefined;

function load(): Promise<Logger | null> {
  logger ??= (async () => {
    const config = firebaseConfig();
    if (!config || doNotTrack()) return null;
    try {
      const [{ initializeApp }, analytics] = await Promise.all([import('firebase/app'), import('firebase/analytics')]);
      if (!(await analytics.isSupported())) return null; // blocked cookies, privacy extensions, old browsers
      const app = initializeApp(config);
      // Single-page app: the first page_view is not automatic, every route
      // change is reported by track('page_view') instead.
      const instance = analytics.initializeAnalytics(app, { config: { send_page_view: false } });
      return (name, params) => analytics.logEvent(instance, name, params);
    } catch {
      return null; // analytics must never break the store
    }
  })();
  return logger;
}

/** Send one GA4 event. Resolves quietly when analytics is off or blocked. */
export async function logAnalyticsEvent(name: string, params: Record<string, unknown> = {}): Promise<void> {
  try {
    const log = await load();
    log?.(name, params);
  } catch {
    /* never block the UI on analytics */
  }
}

/** Test hook: forget the loaded SDK. */
export function resetFirebaseForTests(): void {
  logger = undefined;
}
